/** Fired on cookie banner accept/reject so analytics can re-read `cookieConsent`. */
export const COOKIE_CONSENT_CHANGED_EVENT = 'ra-cookie-consent';

/** Fired after Google Analytics is allowed and its `gtag` queue is available. */
export const GOOGLE_ANALYTICS_READY_EVENT = 'ra-google-analytics-ready';

/**
 * PostHog is limited to the marketing / public site. Auth pages are excluded
 * to avoid recording credentials; admin and other app areas are excluded by path.
 */
const AUTH_PATH_PREFIXES = ['/sign-in', '/forgot-password'] as const;

/** App areas where no analytics (PostHog or Google Analytics) should ever fire. */
const ADMIN_PATH_PREFIXES = ['/admin'] as const;

const MARKETING_PATH_PREFIXES = [
  '/hub',
  '/providers',
  '/logistics-providers',
  '/pricing',
  '/privacy',
  '/terms',
  '/contact',
] as const;

function stripTrailingSlashes(p: string): string {
  if (p.length <= 1) return p || '/';
  return p.replace(/\/+$/, '') || '/';
}

/** True for `/admin` and any nested admin route. Admin is excluded from all analytics. */
export function isAdminPathname(pathname: string): boolean {
  const p = stripTrailingSlashes(pathname);
  return ADMIN_PATH_PREFIXES.some(
    (prefix) => p === prefix || p.startsWith(`${prefix}/`),
  );
}

export function isPostHogEnabledForPathname(pathname: string): boolean {
  const p = stripTrailingSlashes(pathname);
  if (p === '') return true;

  if (isAdminPathname(p)) return false;

  if (AUTH_PATH_PREFIXES.some((auth) => p === auth || p.startsWith(`${auth}/`))) {
    return false;
  }

  if (p === '/') return true;

  for (const prefix of MARKETING_PATH_PREFIXES) {
    if (p === prefix || p.startsWith(`${prefix}/`)) {
      return true;
    }
  }

  return false;
}

export function isAnalyticsCookieConsentAccepted(): boolean {
  if (typeof window === 'undefined') return false;
  return localStorage.getItem('cookieConsent') === 'accepted';
}
